/**
 * "Add from raw material": pick files from the clinic's library and put them on
 * this creative as raws. Links, not copies. The editor never sees this; they see
 * ordinary raws afterwards.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Film, Search, Check } from 'lucide-react';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import { fetchMaterial, linkMaterial, type MediaAsset } from '@/lib/queries';
import { formatBytes } from '@/lib/upload-center';
import { cn } from '@/lib/cn';

export default function MaterialPicker({ creativeId, clientId, onClose, onLinked }: {
  creativeId: string; clientId: string; onClose: () => void; onLinked: () => void;
}) {
  const qc = useQueryClient();
  const rows = useQuery({ queryKey: ['material', clientId, 'picker'], queryFn: () => fetchMaterial(clientId, {}) });
  const [q, setQ] = useState('');
  const [doctor, setDoctor] = useState('');
  const [procedure, setProcedure] = useState('');
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const doctors = useMemo(() => Array.from(new Set((rows.data ?? []).map((a) => a.material_doctor).filter(Boolean) as string[])).sort(), [rows.data]);
  const procedures = useMemo(() => Array.from(new Set((rows.data ?? []).filter((a) => !doctor || a.material_doctor === doctor).map((a) => a.material_procedure).filter(Boolean) as string[])).sort(), [rows.data, doctor]);
  const shown = (rows.data ?? []).filter((a) =>
    (!doctor || a.material_doctor === doctor) && (!procedure || a.material_procedure === procedure)
    && (!onlyUnused || !a.used_count)
    && (!q || a.filename.toLowerCase().includes(q.toLowerCase()) || (a.material_tags ?? []).some((t) => t.toLowerCase().includes(q.toLowerCase()))));

  const link = useMutation({
    mutationFn: () => linkMaterial(creativeId, Array.from(picked)),
    onSuccess: (made) => {
      toast.success(`${made.length} file${made.length === 1 ? '' : 's'} added to this creative`);
      qc.invalidateQueries({ queryKey: ['assets', creativeId] });
      qc.invalidateQueries({ queryKey: ['material', clientId] });
      onLinked();
    },
    onError: () => toast.error('Could not add the files'),
  });
  const toggle = (id: string) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" data-testid="material-picker">
      <div className="w-full sm:max-w-4xl max-h-[92vh] rounded-t-2xl sm:rounded-2xl bg-bg-card border border-slate-800 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div className="text-sm font-medium text-muted-strong inline-flex items-center gap-2"><Film size={14} /> Add from raw material</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-800 text-muted"><X size={16} /></button>
        </div>
        <div className="px-4 py-2 flex flex-wrap items-center gap-2 text-xs border-b border-slate-800">
          <div className="relative flex-1 min-w-[160px]">
            <Search size={12} className="absolute left-2.5 top-2.5 text-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search names and tags" data-testid="picker-search"
              className="w-full h-8 pl-7 pr-2 rounded-lg bg-bg border border-slate-800 text-xs text-muted-strong" />
          </div>
          <select value={doctor} onChange={(e) => { setDoctor(e.target.value); setProcedure(''); }} data-testid="picker-doctor" className="h-8 px-2 rounded-lg bg-bg border border-slate-800 text-xs text-muted-strong">
            <option value="">Any doctor</option>{doctors.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={procedure} onChange={(e) => setProcedure(e.target.value)} data-testid="picker-procedure" className="h-8 px-2 rounded-lg bg-bg border border-slate-800 text-xs text-muted-strong">
            <option value="">Any procedure</option>{procedures.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={() => setOnlyUnused((v) => !v)} data-testid="picker-unused"
            className={cn('px-2.5 py-1.5 rounded-full border', onlyUnused ? 'border-accent-400 text-accent-400' : 'border-slate-800 text-muted')}>Not used yet</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {rows.isLoading && <div className="text-sm text-muted">Loading the library...</div>}
          {rows.isSuccess && shown.length === 0 && <div className="text-sm text-muted">Nothing in the library matches.</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {shown.map((a: MediaAsset) => {
              const on = picked.has(a.id);
              return (
                <button key={a.id} onClick={() => toggle(a.id)} data-testid="picker-item" aria-pressed={on}
                  className={cn('relative rounded-lg overflow-hidden border aspect-square text-left', on ? 'border-accent-400 ring-2 ring-accent-400/40' : 'border-slate-800')}>
                  {a.thumbnail_url ? <img src={a.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-muted/40"><Film size={26} /></div>}
                  <span className={cn('absolute top-1.5 left-1.5 z-10 rounded p-1 ring-1', on ? 'bg-accent-500 text-white ring-white/40' : 'bg-slate-950/70 text-white/70 ring-white/20')}><Check size={12} /></span>
                  {(a.used_count ?? 0) > 0 && <span className="absolute top-1.5 right-1.5 z-10 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/30 text-emerald-200">used in {a.used_count}</span>}
                  <span className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/95 to-transparent p-2">
                    <span className="block text-[10px] text-white/90 truncate">{a.filename}</span>
                    <span className="block text-[9px] text-white/60 truncate">{[a.material_doctor, a.material_procedure, a.material_date].filter(Boolean).join(' · ')}{a.size_bytes ? ` · ${formatBytes(a.size_bytes)}` : ''}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-800 flex items-center gap-3">
          <span className="text-xs text-muted">{picked.size} picked</span>
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" disabled={picked.size === 0 || link.isPending} loading={link.isPending} onClick={() => link.mutate()} data-testid="picker-add">
              Add {picked.size || ''} to this creative
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
